import {Component, OnInit, AfterViewChecked, ViewChild} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {RightSideDrawerService} from '../right-side-drawer.service';
import usStateLocations from '../../../../assets/us-state-locations.json';
import employmentTypes from '../../../../assets/employment-types.json';
import {FormGroup, FormControl, FormBuilder, Validators, AbstractControl} from '@angular/forms';
import {EmployeeInformationService} from 'src/app/data-analysis-summary/employee-information/employee-information.service';
import {EngagementInsigtsService} from 'src/app/engagement-insights/engagement-insights.service';
import {SessionService} from '../../session-service/session.service';
import { formatDate, DatePipe } from '@angular/common';
import { copyObject } from 'src/app/shared/lib/custom-tools';
import { TaxPeriod } from '../../models/engagement/TaxPeriod';
import {ToastrService} from '@kpmg-tax/ui';
import {Messages} from '../../constants/messages';
import {NegativeValidator} from '../../../core/validators/negative-validator';
import {id} from '@swimlane/ngx-datatable';
import { encodeText, decodeText } from 'src/app/shared/lib/custom-tools';
import { State } from './locations.model';


@Component({
  selector: 'add-an-employee-form',
  templateUrl: './add-an-employee-form.component.html',
  styleUrls: ['./add-an-employee-form.component.scss'],
})
export class AddAnEmployeeFormComponent implements OnInit {
  targetForm: FormGroup; // name of formGroup in template
  targetFormSubmitted: boolean = false;
  employmentDatesError: boolean = false; // trigger for CSS
  employeeDatesErrorMessage: string = ''; // MEssage for Dates Error
  employeeNumberError: boolean = false; // trigger for CSS
  currentDate: Date = new Date();
  hireDateRequired: boolean = false;
  hireDate: Date = null;
  termDate: Date = null;
  maxDate: Date = new Date(2999, 25, 12);
  statesunsorted:State[]=[];
  states: string[]=[];
  employment: any;
  companyNames: any;
  @ViewChild('dpStart', {static: true}) dpStart;
  @ViewChild('dpEnd', {static: true}) dpEnd;
  addDisabledLocation: boolean = false;
  addDisabledEmployment: boolean = false;
  noResultsLocation: boolean = false;
  noResultsEmployment: boolean = false;
  displayNoResultsLocation: boolean = false;
  displayNoResultsEmployment: boolean = false;
  isEmpValueEntered: boolean = false;
  addDisabledTaxPeriod: boolean = true;
  noResultsTaxPeriod: boolean = false;
  displayNoResultsTaxPeriod: boolean = false;
  filteredSMEs: any[];
  engagementId: any;
  clientId: any;
  selectedSME: string | undefined;
  isSelected: boolean = false;
  selectedSMEId: any;
  selectedSMEEmail: string;
  companyList: any[];
  SMEList: any[];
   locationMap = new Map<string, string>();
  private unsubscribe$ = new Subject<void>();

  constructor(
    public rightSideDrawerService: RightSideDrawerService,
    public sessionService: SessionService,
    private employeeInformationService: EmployeeInformationService,
    public engagementInsightService: EngagementInsigtsService,
    private formBuilder: FormBuilder,
    public datePipe: DatePipe,
    public toastrService: ToastrService,
    private fb: FormBuilder,
  ) {
   
    
    this.targetForm = this.fb.group({
      location: ['']
    });

    this.employment = employmentTypes.map(function(item) {
      return item.employment;
    }).sort((a, b) => a.localeCompare(b));
    this.engagementId = this.sessionService.getSession('engagementId');
    this.clientId = this.sessionService.getSession('clientId');
    this.employeeInformationService.GetClientUsers(this.clientId).subscribe(
      (data: any) => {
        this.SMEList = data.result;
      }
    );
  }

  public taxPeriods: TaxPeriod[] = this.rightSideDrawerService.taxPeriods;
  public taxPeriodNames: string[] = this.taxPeriods.map(x => x.taxPeriodName);

  ngOnInit() {

    this.employeeInformationService.getLocations().pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe(
      (response:any) => {
        if (response && response.result) {
          this.statesunsorted = response.result as State[];  
              
          this.statesunsorted.forEach(item=>{
            this.locationMap.set(item.code.length === 2 ? item.code : item.name, item.code);
            
           })
                
        
          const twoCharCodes = this.statesunsorted
          .filter(item => item.code.length === 2)
          .map(item => item.code.trim());
  
        const otherCodes = this.statesunsorted
          .filter(item => item.code.length !== 2)
          .map(item => item.name.trim());
  
        
        twoCharCodes.sort((a, b) => a.localeCompare(b));
        otherCodes.sort((a, b) => a.localeCompare(b));    
       
        this.states = [...twoCharCodes, ...otherCodes];     
        
        
          
        }
      }
    );

   
    // reset and create the form group on start up.
    this.targetForm = this.formBuilder.group({
      engagementId: [''],
      employeeNumber: ['', [Validators.required, Validators.maxLength(100)]],
      name: ['', [Validators.required, Validators.maxLength(100)]],
      title: ['', Validators.maxLength(200)],
      type: ['', Validators.maxLength(100)],
      costCenter: ['', Validators.maxLength(100)],
      costCenterDesc: ['', Validators.maxLength(100)],
      location: [''],
      hireDate: [''],
      endDate: [''],
      totalHours: [
        '',
        [Validators.pattern(/^[0-9]\d*$/), NegativeValidator.cannotBeNegativeNumber],
      ],
      wage: ['', [NegativeValidator.cannotBeNegativeNumber]],
      sme: ['', Validators.maxLength(500)],
      company: ['', Validators.maxLength(100)],
      priorYearQualifiedPercentage: ['', [NegativeValidator.cannotBeNegativeNumber]],
      taxPeriod: [{
        value: (this.taxPeriodNames?.length == 1 ? this.taxPeriodNames[0] : ''),
        disabled: this.taxPeriodNames?.length == 1
      }],
    });

    let companyList = this.rightSideDrawerService.legalEntities
      .map(x => x.legalEntityName);
    
    //Pull a list of SMEs that exist within the current employee information table
    let SMEList = this.employeeInformationService.eiTableDataFiltered
      .filter(x => x.sme)
      .map(x => x.sme);

    //Make sure that the company and SME lists only contain unique values, taking case into account.
    //With the current below functions, the first instance of a given string will be used.

    this.companyList = companyList.reduce((result, element) => {
      let normalize = x => (typeof x === 'string' ? x.toLowerCase() : x);

      let normalizedElement = normalize(element);
      if (result.every(otherElement => normalize(otherElement) !== normalizedElement)) {
        result.push(element);
      }

      return result;
    }, []);

    this.SMEList = SMEList.reduce((result, element) => {
      let normalize = x => (typeof x === 'string' ? x.toLowerCase() : x);

      let normalizedElement = normalize(element);
      if (result.every(otherElement => normalize(otherElement) !== normalizedElement)) {
        result.push(element);
      }

      return result;
    }, []);
  }

  get f() {
    return this.targetForm?.controls;
  }

  setStartDate(date: Date) {
    this.hireDate = date;
    if (this.hireDate > this.currentDate) {
      this.employmentDatesError = true;
      this.employeeDatesErrorMessage = Messages.AddEmployeeForm.HIRE_DATE_VALIDATION;
    } else {
      this.employmentDatesError = false;
      this.checkValidDates();
    }
  }

  trimSpecialChar(ctrl: any) {
    if (this.targetForm.get(ctrl).value) {
      if (this.targetForm.get(ctrl) === this.targetForm.get('name')) {
        this.targetForm.controls[ctrl].setValue(
          this.targetForm.get(ctrl).value.replace(/['\x22\x60]+/g, ''),
        );
      } else {
        this.targetForm.controls[ctrl].setValue(
          this.targetForm.get(ctrl).value.replace(/[^a-zA-Z0-9-()._/@, ]/g, ''),
        );
      }
    }
  }

  setEndDate(date: Date) {
    this.termDate = date;
    if (this.termDate) {
      if (this.termDate < this.hireDate) {
        this.employmentDatesError = true;
        this.employeeDatesErrorMessage = Messages.AddEmployeeForm.TERMINATION_DATE_VALIDATION;
      } else {
        this.employmentDatesError = false;
        this.checkValidDates();
      }
    } else if (
      this.employeeDatesErrorMessage === Messages.AddEmployeeForm.TERMINATION_DATE_VALIDATION
    ) {
      this.employmentDatesError = false;
    }
    //this.checkValidDates();
  }

  // Validate start date isn't greater than end date if end date is specified
  checkValidDates() {
    if (this.termDate > this.currentDate) {
      /* if Terrmination Date is after Current Date */ this.employmentDatesError = true;
      this.employeeDatesErrorMessage = Messages.AddEmployeeForm.TERMINATION_DATE_VALIDATION2;
    } else if (this.termDate < this.hireDate && this.termDate) {
      this.employmentDatesError = true;
      this.employeeDatesErrorMessage = Messages.AddEmployeeForm.TERMINATION_DATE_VALIDATION;
    } else {
      this.hireDateRequired = false;
      this.employmentDatesError = false;
    }
  }

  checkHours = () => {
    // If hours are present in form, estimate number to two decimal points
    let totalHours = this.targetForm.get('totalHours').value;

    if (totalHours) {
      totalHours = Math.round(totalHours * 100) / 100;
      this.targetForm.controls.totalHours.setValue(totalHours);
    }
  };

  checkWage = () => {
    let wage = this.targetForm.get('wage').value;

    // If wage is  present in form, estimate number to two decimal points.
    if (wage) {
      wage = Math.round(wage * 100) / 100;
      this.targetForm.controls.wage.setValue(wage);
    }
  };

  // Check if the Employee Number is listed within the engagement.
  checkEmployeeNumber = () => {
    let employeeNo = this.targetForm.get('employeeNumber').value;

    // If employeeNumber field is null or undefined
    if (employeeNo === null || employeeNo === undefined) {
      this.employeeNumberError = true;
      // If employeeNumber is not in the engagement, error is false
    } else {
      this.employeeNumberError = false;
    }
  };

  checkTaxPeriod = () => {
    let taxPeriodValue = this.targetForm.get('taxPeriod').value;
    if (taxPeriodValue) {
      let taxPeriod = (String(taxPeriodValue)).toLocaleUpperCase();
      if (taxPeriod === null || taxPeriod === undefined || taxPeriod == '') {
        this.addDisabledTaxPeriod = true;
      } else if (this.taxPeriodNames.some(x => x.toLocaleUpperCase() == taxPeriod)) {
        this.addDisabledTaxPeriod = false;
      }
    }
    else {
      this.addDisabledTaxPeriod = true;
    }
  }

  // Delete empty properties to clean form data to send to back-end API
  // Blank values sent to the back-end API gives errors.
  cleanTargetFormToPost = () => {
    // Make a copy of what the form holds
    this.targetForm.value.engagementId = this.sessionService.getSession('engagementId');
    let formData = this.sessionService.removeNullEmptyValues(copyObject(this.targetForm.value));
    formData.hireDate = formData.hireDate
      ? this.datePipe.transform(formData.hireDate, 'MM/dd/yyyy')
      : formData.engagementStartDate;
    formData.endDate = formData.endDate
      ? this.datePipe.transform(formData.endDate, 'MM/dd/yyyy')
      : formData.engagementEndDate;
    if (this.taxPeriodNames?.length == 1) {
      formData.taxPeriod = this.taxPeriodNames[0];
    }
    // return the new JSON object. This is to be sent to the back-end API
    return formData;
  };

  // This function runs when "Add Employee" button is clicked
  postRightSideDrawerData = () => {
    // Reset Employment Date Error trigger
    this.addDisabledLocation = true;
    this.addDisabledEmployment = true;
    this.addDisabledTaxPeriod = true;
    this.employmentDatesError = false;
    const employeeData = this.cleanTargetFormToPost();
    employeeData.employeeNumber = encodeText(employeeData.employeeNumber);
    employeeData.name = encodeText(employeeData.name);
    employeeData.title = encodeText(employeeData.title);
    employeeData.type = encodeText(employeeData.type);
    employeeData.costCenter = encodeText(employeeData.costCenter);
    employeeData.costCenterDesc = encodeText(employeeData.costCenterDesc);
    employeeData.company = encodeText(employeeData.company);
    employeeData.taxPeriodId = this.taxPeriods
      .find(x => x.taxPeriodName.trim().toLowerCase() == employeeData.taxPeriod.trim().toLowerCase())?.taxPeriodId;
    employeeData.taxPeriod = '';
    employeeData.location=this.locationMap.get(this.targetForm.get('location').value)
    this.employeeInformationService.postEmpData(employeeData).subscribe(
      (data: any) => {
        if (data.success) {
          // Refresh data table on page
          this.toastrService.setSuccessMessage('Employee added successfully.');
          this.employeeInformationService.refreshTable();
          this.rightSideDrawerService.closeRightSideDrawer();
        } else {
          this.addDisabledLocation = false;
          this.addDisabledEmployment = false;
          this.toastrService.setErrorMessage(data.errorMessage);
        }
      },
      // Show in console if there is an error given from the back end.
      error => {
        this.addDisabledLocation = false;
        this.addDisabledEmployment = false;
      },
    );

    // Refresh data table on page
    //this.employeeInformationService.refreshTable();

    // Change variable to note a form submission attempt was made.
    this.targetFormSubmitted = true;
  };

  // reset form content.
  public resetRightSideDrawerData = () => {
    this.targetForm.reset();
    // Reset CSS triggers
    this.employeeNumberError = false;
    this.employmentDatesError = false;
    this.targetFormSubmitted = false;
  };

  // Search filter function for TypeAhead text input. Used for Location input.
  searchLocation = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      tap(term => {
        // Remove "no results" message in case it was displayed from the previous search
        this.noResultsLocation = false;
        this.displayNoResultsLocation = false;
      }),
      switchMap(term => {
        // Process the search term entered
        return this.searchLocations(term).pipe(
          switchMap(data => {
            this.noResultsLocation = data.length === 0;
            if (this.noResultsLocation) {
              this.addDisabledLocation = true;
              this.displayNoResultsLocation = true;
            } else {
              /* Explanation: The dropdown menu looks to see if the term matches any state codes; however, the current scenario means that, in the case where the user types
              in something that could be part of a state code (Example: the user types in 'A' and nothing else), it is accepted as a state code.

              In the case where the user's input is not an exact match to the state code, we disable the "Save Employee" button. However, we create the "displayNoResultsLocation"
              variable to make sure that the "No Location Found" error does not pop up in the middle of what the user was typing.
              */
              if (
                (this.states.findIndex(item => item.toLowerCase() === term.toLowerCase()) !==
                  -1) ===
                  false &&
                term !== ''
              ) {
                this.addDisabledLocation = true;
              } else {
                this.addDisabledLocation = false;
              }
            }
            return of(data);
          }),
          catchError(() => {
            this.noResultsLocation = true;
            return of([]);
          }),
        );
      }),
    );

    public searchLocations(term: string): Observable<any> {
      return of(this.states.filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1));
    }


  searchEmploymentType = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      tap(term => {
        // Remove "no results" message in case it was displayed from the previous search
        this.noResultsEmployment = false;
        this.addDisabledEmployment = false;
        this.displayNoResultsEmployment = false;
      }),
      switchMap(term => {
        // Process the search term entered
        return this.searchEmploymentTypes(term).pipe(
          switchMap(data => {
            this.noResultsEmployment = data.length === 0;
            if (this.noResultsEmployment) {
              this.addDisabledEmployment = true;
              this.displayNoResultsEmployment = true;
            } else {
              /* Explanation: The dropdown menu looks to see if the term matches any employment types; however, the current scenario means that, in the case where the user types
              in something that could be part of a employment type (Example: the user types in 'A' and nothing else), it is accepted as an employment type.

              In the case where the user's input is not an exact match to the employment type, we disable the "Save Employee" button. However, we create the 
              "displayNoResultsEmployment" variable to make sure that the "No Employment Type Found" error does not pop up in the middle of what the user was typing.
              */
              if (
                (this.employment.findIndex(item => item.toLowerCase() === term.toLowerCase()) !==
                  -1) ===
                  false &&
                term !== ''
              ) {
                this.addDisabledEmployment = true;
              } else {
                this.addDisabledEmployment = false;
              }
            }
            return of(this.employment);
          }),
          catchError(() => {
            this.noResultsLocation = true;
            return of([]);
          }),
        );
      }),
    );

  public searchEmploymentTypes(term: string): Observable<any> {
    return of(this.employment.filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1));
  }

  searchSMEStream = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      switchMap(term => {
        return this.searchSMEs(term);
      }),
    );

  public searchSMEs(term: string): Observable<any> {
    let temList = this.SMEList;
    if (temList) {
      return of(
        temList
          .filter(item => item.displayName.toLowerCase().includes(term.toLowerCase()))
          .map(x => {
            return x.displayName;
          }),
      );
    } else {
      return of([]);
    }
  }

  searchCompany = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      switchMap(term => {
        // Process the search term entered
        return this.searchCompanies(term).pipe(
          switchMap(data => {
            return of(data);
          }),
          catchError(() => {
            return of([]);
          }),
        );
      }),
    );

  public searchCompanies(term: string): Observable<any> {
    return of(this.companyList.filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1));
  }

  searchTaxPeriod = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      tap(term => {
        // Remove "no results" message in case it was displayed from the previous search
        this.noResultsTaxPeriod = false;
        this.displayNoResultsTaxPeriod = false;
      }),
      switchMap((term: string) => {
        // Process the search term entered
        return this.searchTaxPeriodNames(term).pipe(
          switchMap((data: string) => {
            this.noResultsTaxPeriod = data.length === 0;
            if (this.noResultsTaxPeriod) {
              this.addDisabledTaxPeriod = true;
              this.displayNoResultsTaxPeriod = true;
            } else {
              if (
                (this.taxPeriodNames.findIndex(
                  item => item.toLowerCase() === term.toLowerCase(),
                ) !==
                  -1) ===
                false &&
                term !== ''
              ) {
                this.addDisabledTaxPeriod = true;
              } else if (term == null || term == undefined || term == '') {
                this.addDisabledTaxPeriod = true;
              } else {
                this.addDisabledTaxPeriod = false;
              }
            }
            return of(this.taxPeriodNames);
          }),
          catchError(() => {
            this.noResultsTaxPeriod = true;
            return of([]);
          }),
        );
      }),
    )

  public searchTaxPeriodNames(term: string): Observable<any> {
    return of(this.taxPeriodNames
      .filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1));
  }


  /*
    public searchCompanies(term: string): Observable<any> {
      return of(
        this.employeeInformationService.eiTableDataFiltered.filter(y => y.toLowerCase().includes(term.toLowerCase()
      )),
      );
    }
    */

  // ngAfterViewChecked() {
  //   // Refresh form inputs if drawer is opened or loaded.
  //   if (this.rightSideDrawerService.triggerRightSideDrawerClick) {
  //     this.rightSideDrawerService.triggerRightSideDrawerClick = false; // Set trigger to false so that this action is only performed once. Without this, the form would keep resetting on every change on any input.
  //     this.resetRightSideDrawerData();
  //   }
  // }

  checkDates() {
    if (
      (this.targetForm.controls.endDate.value === null ||
        this.targetForm.controls.endDate.value === '') &&
      this.employmentDatesError
    ) {
      this.employmentDatesError = false;
    }
  }

  openDatePickerEnd() {
    this.dpEnd.toggle();
  }

  openDatePickerStart() {
    this.dpStart.toggle();
  }

  onfocStartDate() {
    setTimeout(() => {
      this.dpStart?.show();
    }, 100);
  }

  onfocEndDate() {
    setTimeout(() => {
      this.dpEnd?.show();
      this.dpStart.hide();
    }, 100);
  }

  onfocEF() {
    this.dpEnd.hide();
  }

  clearStartDate() {
    this.targetForm.controls.hireDate.setValue(null);
  }
  clearEndDate() {
    this.targetForm.controls.endDate.setValue(null);
    this.employmentDatesError = false;
  }

  dropdownInputChanged() {
    setTimeout(() => this.inputChanged(), 100);
  }

  inputChanged() {
    this.checkTaxPeriod();
    let employee = {
      employeeNumber: this.targetForm.get('employeeNumber').value,
      name: this.targetForm.get('name').value,
      title: this.targetForm.get('title').value,
      type: this.targetForm.get('type').value,
      costCenter: this.targetForm.get('costCenter').value,
      costCenterDesc: this.targetForm.get('costCenterDesc').value,
      location: this.locationMap.get(this.targetForm.get('location').value),
      hireDate: this.targetForm.get('hireDate').value,
      endDate: this.targetForm.get('endDate').value,
      totalHours: this.targetForm.get('totalHours').value,
      wage: this.targetForm.get('wage').value,
      sme: this.targetForm.get('sme').value,
      company: this.targetForm.get('company').value,
      taxPeriod: this.targetForm.get('taxPeriod').value,
      priorYearQualifiedPercentage: this.targetForm.get('priorYearQualifiedPercentage').value,
    };

    if (
      (this.states.findIndex(
        item => item.toLowerCase() === this.targetForm.get('location').value.toLowerCase(),
      ) ===
        -1) ===
        false ||
        this.targetForm.get('location').value.toLowerCase() === ''
    ) {
      this.addDisabledLocation = false;
      this.displayNoResultsLocation = false;
    }
    if (
      (this.employment.findIndex(
        item => item.toLowerCase() === this.targetForm.get('type').value.toLowerCase(),
      ) ===
        -1) ===
        false ||
      this.targetForm.get('type').value === ''
    ) {
      this.addDisabledEmployment = false;
      this.displayNoResultsEmployment = false;
    }

    if (
      employee.employeeNumber !== null ||
      employee.name !== null ||
      employee.title !== null ||
      employee.type !== null ||
      employee.costCenter !== null ||
      employee.costCenterDesc !== null ||
      employee.location !== null ||
      employee.hireDate !== null ||
      employee.endDate !== null ||
      employee.totalHours !== null ||
      employee.wage !== null ||
      employee.sme !== null ||
      employee.company !== null ||
      employee.priorYearQualifiedPercentage !== null
    ) {
      this.rightSideDrawerService.setMandatoryField(true, 'Add Employee');
    } else {
      this.rightSideDrawerService.setMandatoryField(false, '');
    }
  }
  isValueEntered() {
    let employeeName = this.targetForm.get('name').value;
    let employeeNumber = this.targetForm.get('employeeNumber').value;
    if (
      employeeName &&
      employeeName.trim().length > 0 &&
      employeeNumber &&
      employeeNumber.trim().length > 0
    ) {
      this.isEmpValueEntered = true;
    } else {
      this.isEmpValueEntered = false;
    }
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
